// Which confirmed settlements hold a cost, the way every cost screen reads it:
// the cost lists (CostsTab, CostsOverview), the cost detail (InvoiceViewModal,
// the PropertyCost drawer), the cost's edit form and its delete.
//
// A regular settlement settles the cost: the cost is "in a settlement" and
// frozen, for edits and deletes alike. A departure settlement (see
// ISettledDeparture) settled one lease's share and left the rest open (Lewis,
// 2026-10-09): the cost is partly settled, it can still be changed (the
// departure keeps its share, the next settlement gives the owner any
// difference) and it cannot be deleted before that departure. A departure
// that froze no share of a cost it lists settled nothing of it; only the
// delete stays refused, since the departure still lists the cost. The API
// holds the same rules (propertyCostUpdateById, propertyCostDeleteById).

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { uniqueById } from "./costsList.utils";
import { roundCents } from "./money.utils";
import { settlementDisplayName } from "./settlementName.utils";
import { ISettledDeparture, Settlement, settledDepartureOf } from "./types";

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

// A departed lease's share of one cost, as its departure settlement froze it.
export interface ICostDepartureShare {
  settlementId: string;
  settlementName: string;
  // The departed lease, its tenant and its unit: the owner of that unit
  // gets what a change does to the share (M45).
  contractId: string;
  tenantName: string;
  unitId: string;
  // The lease's last day the departure settled, yyyy-mm-dd.
  until: string;
  // When the departure settlement was confirmed.
  confirmedAt?: string;
  // The lease's share of the cost, excl. VAT.
  amount: number;
}

export interface ICostSettlementState {
  // The confirmed settlement that settled the cost, a departure aside.
  settledIn?: Settlement;
  // The departure settlements that hold a share of it, earliest first.
  departures: ICostDepartureShare[];
}

const NO_SETTLEMENT: ICostSettlementState = { departures: [] };

// The settlement state of every cost the confirmed `settlements` hold.
// The screens pass what they fetched, which may list a settlement twice.
export const costSettlementStates = (
  settlements: Settlement[],
): Map<string, ICostSettlementState> => {
  const states = new Map<string, ICostSettlementState>();
  const stateOf = (costId: string) => {
    const state = states.get(costId) ?? { departures: [] };
    states.set(costId, state);
    return state;
  };
  for (const settlement of uniqueById(settlements)) {
    if (settlement.status !== "confirmed" || !settlement.snapshot) continue;
    const departure = settledDepartureOf(settlement);
    for (const costId of new Set(settlement.snapshot.invoiceIds ?? [])) {
      if (!departure) {
        stateOf(costId).settledIn ??= settlement;
        continue;
      }
      // A departure holds only the share it froze for its lease, the way a
      // later settlement reads it (engine.ts): a cost it lists that gave the
      // lease nothing (an owner's cost, a key weight of 0) stays open.
      const share = departure.costByInvoice[costId];
      if (share == null) continue;
      stateOf(costId).departures.push({
        settlementId: settlement.id,
        settlementName: settlementDisplayName(settlement),
        contractId: departure.contractId,
        tenantName: departure.tenantName,
        unitId: departure.unitId,
        until: departure.until,
        confirmedAt: settlement.confirmedAt,
        amount: roundCents(share),
      });
    }
  }
  for (const state of states.values()) {
    state.departures.sort((a, b) => a.until.localeCompare(b.until));
  }
  return states;
};

// The shares of each cost that `departures` froze, earliest departure
// first: what a settlement's cost choice shows of the departures it accounts
// for (departuresInScope), the same ones its calculation takes off. Named
// as the lists name their settlement (`settlements`).
export const departureSharesByCost = (params: {
  departures: ISettledDeparture[];
  settlements: Settlement[];
}): Record<string, ICostDepartureShare[]> => {
  const { departures, settlements } = params;
  const byCost: Record<string, ICostDepartureShare[]> = {};
  for (const departure of departures) {
    const settlement = settlements.find(
      candidate => candidate.id === departure.settlementId,
    );
    for (const [costId, share] of Object.entries(departure.costByInvoice)) {
      (byCost[costId] ??= []).push({
        settlementId: departure.settlementId,
        settlementName: settlement
          ? settlementDisplayName(settlement)
          : departure.name ?? "",
        contractId: departure.contractId,
        tenantName: departure.tenantName,
        unitId: departure.unitId,
        until: departure.until,
        confirmedAt: settlement?.confirmedAt,
        amount: roundCents(share),
      });
    }
  }
  for (const shares of Object.values(byCost)) {
    shares.sort((a, b) => a.until.localeCompare(b.until));
  }
  return byCost;
};

export const costSettlementStateOf = (
  states: Map<string, ICostSettlementState>,
  costId: string,
): ICostSettlementState => states.get(costId) ?? NO_SETTLEMENT;

// What a cost's status shows of its settlements: settled, or the departures
// that settled part of it while the rest is still to settle.
export interface ICostSettledInfo {
  settled: boolean;
  // The day the settlement that settled it was confirmed.
  date?: string;
  // Only while no regular settlement settled the cost.
  departures?: ICostDepartureShare[];
}

export const costSettledInfo = (
  state: ICostSettlementState,
): ICostSettledInfo =>
  state.settledIn
    ? { settled: true, date: state.settledIn.confirmedAt ?? "" }
    : { settled: false, departures: state.departures };

// What a cost's detail shows, in one state for its status, its tenants and
// its banners: the regular settlement found holding the cost, else what the
// screen that opened it knows (a settlement's own view, which may answer
// before the detail fetched it), else the departures that settled part of it.
export const costDetailSettledInfo = (params: {
  state: ICostSettlementState;
  openedFrom?: Pick<ICostSettledInfo, "settled" | "date">;
}): ICostSettledInfo => {
  const { state, openedFrom } = params;
  if (state.settledIn) return costSettledInfo(state);
  if (openedFrom?.settled) return { settled: true, date: openedFrom.date };
  return { settled: false, departures: state.departures };
};

// Departure settlements hold part of the cost and nothing settled the rest.
export const isPartlySettledAtDeparture = (
  info: Pick<ICostSettledInfo, "settled" | "departures">,
): boolean => !info.settled && (info.departures ?? []).length > 0;

// "Departure Hugo, until 30/06/2026", one per departure, for under a status.
export const departureShareLabel = (share: ICostDepartureShare): string =>
  getLocalizedText("costs.status.partly_settled_departure_detail", {
    tenant: share.tenantName,
    until: fmtDate(share.until),
  });
